// src/pages/GerarDocumentos/hooks/useDocumentGeneration.js
import { useState, useEffect } from 'react';
import axios from 'axios';

/**
 * Constrói o campo `instrucoes` com os blocos de anexos transcritos.
 * Formato: "instrucoes\n\n**FICHEIRO ANEXO: nome**\nCONTEUDO: conteudo"
 */
function buildInstrucoesComAnexos(instrucoes, anexos) {
  const base = instrucoes.trim();
  if (!anexos || anexos.length === 0) return base;
  const blocos = anexos
    .map(a => `**FICHEIRO ANEXO: ${a.nome}**\nCONTEUDO: ${a.conteudo}`)
    .join('\n\n');
  return base ? `${base}\n\n${blocos}` : blocos;
}

const useDocumentGeneration = ({ config, anexosTranscritos, clienteAtual }) => {
  const [codCliente, setCodCliente] = useState('');
  const [responseText, setResponseText] = useState('');
  const [documentData, setDocumentData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [tipoDocumento, setTipoDocumento] = useState('procuracao');
  const [nomeDocumentoCustomizado, setNomeDocumentoCustomizado] = useState('');
  const [instrucoesAdicionais, setInstrucoesAdicionais] = useState('');

  useEffect(() => {
    const codigoCtx = clienteAtual?.codCliente;
    if (codigoCtx && !codCliente) {
      setCodCliente(codigoCtx);
    }
  }, [clienteAtual?.codCliente, codCliente]);

  const handleTipoDocumentoChange = (tipo) => {
    setTipoDocumento(tipo);
    setError('');
    setResponseText('');
    setDocumentData(null);
    setNomeDocumentoCustomizado('');
    setInstrucoesAdicionais('');
  };

  const isFormValid = () => {
    if (!config.advogado.trim() || !config.oa.trim()) return false;
    if (tipoDocumento === 'procuracao') return codCliente.trim().length > 0;
    return nomeDocumentoCustomizado.trim().length > 0;
  };

  const gerarProcuracao = async () => {
    if (!codCliente.trim()) {
      setError('Por favor, informe o código do cliente');
      return;
    }
    if (!config.advogado.trim() || !config.oa.trim()) {
      setError('Por favor, configure os dados do advogado');
      return;
    }
    setLoading(true);
    setError('');
    setResponseText('Loading...');
    setDocumentData(null);
    try {
      const formattedCode = codCliente.padStart(5, '0');
      const requestData = {
        type: '02',
        cod_cliente: formattedCode,
        subtype: '01',
        advogado: config.advogado,
        oa: config.oa,
        domicilio: config.domicilio,
        modeloIdentificarDados: config.modeloIdentificarDados,
        modeloCriarProcuracao: config.modeloCriarProcuracao,
        pageText: '',
        pageUrl: ''
      };
      const response = await axios.post('/api/documentos/gerar-procuracao', requestData);
      if (response.data.success) {
        const data = response.data.data;
        if (data.text) setResponseText(data.text);
        if (data.document?.content && data.document?.filename) {
          setDocumentData({ content: data.document.content, filename: data.document.filename });
        }
      } else {
        setError(response.data.message || 'Erro ao gerar procuração');
        setResponseText('#ERRO AO GERAR# ' + (response.data.message || 'Erro desconhecido'));
      }
    } catch (err) {
      console.error('Error generating document:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError('Erro ao gerar procuração: ' + errorMessage);
      setResponseText('#ERRO AO GERAR# ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const gerarDocumentoGenerico = async () => {
    if (!nomeDocumentoCustomizado.trim()) {
      setError('Por favor, informe o nome do documento');
      return;
    }
    if (!config.advogado.trim() || !config.oa.trim()) {
      setError('Por favor, configure os dados do advogado');
      return;
    }
    setLoading(true);
    setError('');
    setResponseText('A gerar documento (pode demorar alguns minutos)...');
    setDocumentData(null);
    try {
      const instrucoesCompletas = buildInstrucoesComAnexos(
        instrucoesAdicionais,
        anexosTranscritos
      );

      const requestData = {
        nomeDocumento: nomeDocumentoCustomizado.trim(),
        instrucoes: instrucoesCompletas,
        advogado: config.advogado,
        oa: config.oa,
        domicilio: config.domicilio,
        cod_cliente: codCliente ? parseInt(codCliente) : null
      };

      // POST retorna 202 imediatamente com job_id
      const response = await axios.post('/api/documentos/gerar-generico', requestData);
      if (!response.data.success) {
        throw new Error(response.data.message || 'Erro ao iniciar geração');
      }

      const jobId = response.data.job_id;

      // Polling até completar (máx 5 min: 60 × 5s)
      for (let i = 0; i < 60; i++) {
        await new Promise(r => setTimeout(r, 5000));
        const poll = await axios.get(`/api/documentos/job/${jobId}`);
        const { estado, resultado } = poll.data;

        if (estado === 'done') {
          setResponseText(resultado);
          return;
        }
        if (estado === 'error') {
          throw new Error('Falha na geração do documento pelo servidor');
        }
      }
      throw new Error('Timeout: documento não gerado em 5 minutos');

    } catch (err) {
      console.error('Error generating document:', err);
      const errorMessage = err.response?.data?.message || err.message;
      setError('Erro ao gerar documento: ' + errorMessage);
      setResponseText('#ERRO AO GERAR# ' + errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const gerarDocx = async () => {
    if (!responseText || responseText.startsWith('#ERRO')) {
      setError('Nenhum conteúdo válido para gerar DOCX');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const nomeArquivo = tipoDocumento === 'outros'
        ? nomeDocumentoCustomizado.trim().replace(/\s+/g, '_')
        : 'procuracao';
      const payload = {
        texto_conteudo: responseText,
        modelo_path: 'modelo_geral.docx',
        nome_arquivo_base: nomeArquivo,
        return_as: 'bytes'
      };
      const response = await axios.post(
        'https://api-sniperspt.pythonanywhere.com/api/document/generate_from_text',
        payload,
        {
          headers: { 'X-API-KEY': 'APIKEY##', 'Content-Type': 'application/json' },
          responseType: 'blob',
          timeout: 60000
        }
      );
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${nomeArquivo}.docx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Erro ao gerar DOCX:', err);
      setError('Erro ao gerar arquivo DOCX');
    } finally {
      setLoading(false);
    }
  };

  const handleGerarDocumento = () => {
    if (tipoDocumento === 'procuracao') {
      gerarProcuracao();
    } else {
      gerarDocumentoGenerico();
    }
  };

  const downloadDocument = () => {
    if (!documentData) return;
    try {
      const byteCharacters = atob(documentData.content);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.style.display = 'none';
      a.href = url;
      a.download = documentData.filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      console.error('Error downloading document:', err);
      setError('Erro ao fazer download do documento');
    }
  };

  const copyToClipboard = () => {
    if (!responseText) return;
    navigator.clipboard.writeText(responseText).then(() => {
      const button = document.querySelector('.copy-btn');
      if (button) {
        const originalText = button.textContent;
        button.textContent = '✅ Copiado!';
        button.style.background = 'linear-gradient(135deg, #27ae60, #2ecc71)';
        setTimeout(() => {
          button.textContent = originalText;
          button.style.background = 'linear-gradient(135deg, #3498db, #2980b9)';
        }, 2000);
      }
    }).catch(err => {
      console.error('Erro ao copiar:', err);
      setError('Erro ao copiar para área de transferência');
    });
  };

  return {
    codCliente,
    setCodCliente,
    responseText,
    documentData,
    loading,
    error,
    setError,
    tipoDocumento,
    nomeDocumentoCustomizado,
    setNomeDocumentoCustomizado,
    instrucoesAdicionais,
    setInstrucoesAdicionais,
    handleTipoDocumentoChange,
    isFormValid,
    handleGerarDocumento,
    downloadDocument,
    copyToClipboard,
    gerarDocx
  };
};

export default useDocumentGeneration;
